import type { Id, Project, SettingsBag } from '../core/model';
import type { AssetMeta, AssetStore, ProjectBundler } from './api';
import { migrateProject } from './migrate';
import { createZip, readZip, type ZipEntry, type ZipInput, type ZipLimits } from './zip';

const BUNDLE_FORMAT = 'ohmyobs-bundle';
const BUNDLE_VERSION = 1;
const MAX_PROJECT_JSON_BYTES = 64 * 1024 * 1024;
const MAX_ASSET_COUNT = 5_000;
const ASSET_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/** Export enforces the same limits, so a bundle the app writes is always one it can read back. */
const IMPORT_LIMITS: ZipLimits = {
  maxEntries: MAX_ASSET_COUNT + 2,
  maxEntryBytes: 2 * 1024 ** 3,
  maxTotalBytes: 4 * 1024 ** 3 - 1,
};
const ZIP_OVERHEAD_PER_ENTRY = 30 + 46 + 2 * 128;

interface BundleAssetEntry {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: number;
}

interface AssetsManifest {
  format: typeof BUNDLE_FORMAT;
  version: number;
  assets: BundleAssetEntry[];
}

type Json = unknown;

function mapStrings(value: Json, fn: (s: string) => string): Json {
  if (typeof value === 'string') return fn(value);
  if (Array.isArray(value)) return value.map((v) => mapStrings(v, fn));
  if (typeof value === 'object' && value !== null) {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, mapStrings(v, fn)]));
  }
  return value;
}

function collectStrings(value: Json, into: Set<string>): void {
  if (typeof value === 'string') into.add(value);
  else if (Array.isArray(value)) for (const v of value) collectStrings(v, into);
  else if (typeof value === 'object' && value !== null) for (const v of Object.values(value)) collectStrings(v, into);
}

/** Every settings bag in the document: sources, filters on sources and scenes, transitions, plugin data. */
function forEachSettingsBag(project: Project, fn: (bag: SettingsBag) => SettingsBag | void): void {
  const apply = (holder: { settings: SettingsBag }) => {
    holder.settings = fn(holder.settings) ?? holder.settings;
  };
  for (const source of Object.values(project.sources)) {
    apply(source);
    source.filters.forEach(apply);
  }
  for (const scene of Object.values(project.scenes)) scene.filters.forEach(apply);
  for (const t of Object.values(project.transitions)) apply(t);
  for (const [id, data] of Object.entries(project.pluginData)) project.pluginData[id] = fn(data) ?? data;
}

function referencedAssetIds(project: Project, known: ReadonlySet<string>): Set<string> {
  const strings = new Set<string>();
  forEachSettingsBag(structuredClone(project), (bag) => void collectStrings(bag, strings));
  return new Set([...strings].filter((s) => known.has(s)));
}

function remapAssetIds(project: Project, mapping: ReadonlyMap<string, string>): void {
  if (mapping.size === 0) return;
  forEachSettingsBag(project, (bag) => mapStrings(bag, (s) => mapping.get(s) ?? s) as SettingsBag);
}

/** Run state (a running countdown, a playlist position) belongs to this machine's show, not to a portable file. */
function stripRuntimeState(project: Project): void {
  for (const source of Object.values(project.sources)) delete source.runtimeState;
}

function stripSecrets(project: Project): void {
  project.output.streaming.whipBearerToken = '';
  // Stream keys are commonly embedded in the endpoint path or query.
  project.output.streaming.whipEndpoint = '';
  project.output.streaming.swarmStreamKey = '';
  project.output.streaming.iceServers = project.output.streaming.iceServers.map(({ urls }) => ({ urls }));
}

function newProjectId(): Id {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 16);
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function parseManifest(raw: unknown): AssetsManifest {
  if (!isRecord(raw) || raw.format !== BUNDLE_FORMAT) throw new Error('assets.json is not a project bundle manifest');
  if (raw.version !== BUNDLE_VERSION) throw new Error(`Unsupported bundle version ${String(raw.version)}`);
  if (!Array.isArray(raw.assets)) throw new Error('assets.json is malformed');
  if (raw.assets.length > MAX_ASSET_COUNT) throw new Error('Bundle lists too many assets');
  const seen = new Set<string>();
  const assets = raw.assets.map((a): BundleAssetEntry => {
    if (
      !isRecord(a) ||
      typeof a.id !== 'string' ||
      !ASSET_ID_PATTERN.test(a.id) ||
      typeof a.name !== 'string' ||
      typeof a.mimeType !== 'string' ||
      typeof a.sizeBytes !== 'number' ||
      !Number.isFinite(a.sizeBytes) ||
      a.sizeBytes < 0 ||
      typeof a.createdAt !== 'number'
    ) {
      throw new Error('assets.json contains an invalid asset entry');
    }
    if (seen.has(a.id)) throw new Error(`assets.json lists asset "${a.id}" twice`);
    seen.add(a.id);
    return { id: a.id, name: a.name, mimeType: a.mimeType, sizeBytes: a.sizeBytes, createdAt: a.createdAt };
  });
  return { format: BUNDLE_FORMAT, version: BUNDLE_VERSION, assets };
}

export function createProjectBundler(assets: AssetStore): ProjectBundler {
  const encoder = new TextEncoder();

  return {
    async exportBundle(project, { includeAssets }) {
      const copy = structuredClone(project);
      stripSecrets(copy);
      stripRuntimeState(copy);

      const files: ZipInput[] = [];
      const manifest: AssetsManifest = { format: BUNDLE_FORMAT, version: BUNDLE_VERSION, assets: [] };
      if (includeAssets) {
        const metas = await assets.list();
        const byId = new Map<string, AssetMeta>(metas.map((m) => [m.id, m]));
        for (const id of referencedAssetIds(copy, new Set(byId.keys()))) {
          const blob = await assets.getBlob(id);
          if (!blob) throw new Error(`Asset "${id}" is referenced by the project but its data is missing`);
          const meta = byId.get(id)!;
          manifest.assets.push({ id, name: meta.name, mimeType: meta.mimeType, sizeBytes: blob.size, createdAt: meta.createdAt });
          files.push({ name: `assets/${id}`, data: blob });
        }
      }
      // Asset entries are lazy views onto the stored files: consume the result (download or copy it)
      // before deleting those assets.
      const projectJson = encoder.encode(JSON.stringify(copy));
      if (projectJson.byteLength > MAX_PROJECT_JSON_BYTES) throw new Error('The project document is too large to export');
      if (files.length > MAX_ASSET_COUNT) throw new Error(`Too many assets to export (limit ${MAX_ASSET_COUNT})`);
      let total = projectJson.byteLength;
      for (const f of files) {
        const size = (f.data as Blob).size;
        if (size > IMPORT_LIMITS.maxEntryBytes) throw new Error(`Asset ${f.name} is too large to export (limit 2 GiB)`);
        total += size + ZIP_OVERHEAD_PER_ENTRY;
      }
      if (total > IMPORT_LIMITS.maxTotalBytes) throw new Error('The assets are too large for one bundle (limit 4 GiB); export without assets');
      return createZip([
        { name: 'project.json', data: projectJson },
        { name: 'assets.json', data: encoder.encode(JSON.stringify(manifest)) },
        ...files,
      ]);
    },

    async importBundle(file) {
      let entries: ZipEntry[];
      try {
        entries = await readZip(file, IMPORT_LIMITS);
      } catch (err) {
        throw new Error(`Not a valid project bundle: ${err instanceof Error ? err.message : String(err)}`);
      }
      const byName = new Map(entries.map((e) => [e.name, e]));

      const projectEntry = byName.get('project.json');
      if (!projectEntry) throw new Error('Bundle has no project.json');
      if (projectEntry.size > MAX_PROJECT_JSON_BYTES) throw new Error('project.json is too large');
      let project: Project;
      try {
        project = migrateProject(JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(await projectEntry.bytes())));
      } catch (err) {
        throw new Error(`Bundle project is invalid: ${err instanceof Error ? err.message : String(err)}`);
      }

      const manifestEntry = byName.get('assets.json');
      let manifest: AssetsManifest = { format: BUNDLE_FORMAT, version: BUNDLE_VERSION, assets: [] };
      if (manifestEntry) {
        try {
          manifest = parseManifest(JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(await manifestEntry.bytes())));
        } catch (err) {
          throw new Error(`Bundle asset list is invalid: ${err instanceof Error ? err.message : String(err)}`);
        }
      }

      // Verify every asset's integrity before touching the store so a bad bundle imports nothing.
      const blobs = new Map<string, Blob>();
      for (const meta of manifest.assets) {
        const entry = byName.get(`assets/${meta.id}`);
        if (!entry) throw new Error(`Bundle is missing data for asset "${meta.id}"`);
        const blob = await entry.blob().catch((err: unknown) => {
          throw new Error(`Bundle asset "${meta.id}" is corrupt: ${err instanceof Error ? err.message : String(err)}`);
        });
        blobs.set(meta.id, new Blob([blob], { type: meta.mimeType }));
      }

      const before = new Set((await assets.list()).map((m) => m.id));
      const mapping = new Map<string, string>();
      try {
        for (const meta of manifest.assets) {
          const stored = await assets.put(blobs.get(meta.id)!, meta.name);
          mapping.set(meta.id, stored.id);
        }
      } catch (err) {
        for (const stored of mapping.values()) {
          if (!before.has(stored)) await assets.delete(stored).catch(() => undefined);
        }
        throw new Error(`Importing assets failed: ${err instanceof Error ? err.message : String(err)}`);
      }

      remapAssetIds(project, mapping);
      stripSecrets(project);
      stripRuntimeState(project);
      return { ...project, id: newProjectId(), updatedAt: Date.now() };
    },
  };
}
